// Adım 2 — Eşleştir yardımcı panelleri (ImportMappingStep.tsx'ten ayrıldı — güvenlik/UX taraması
// 2026-10-08, 600 satır sınırı; davranış birebir): sayfa sekmeleri + "bu sayfayı atla" (P2-8),
// varsayılan yıl/işletme fieldset'i (B-02/B-03, P2-6 sayfa bazlı yıl) ve sayfalama / otomatik
// kayıt alt satırı. Durum ve kararlar ImportMappingStep'te kalır; buraya yalnız prop gelir.

import type { ClientEntity, KpiImportGrid, KpiMappingDefaults, KpiMappingRow } from "@mahir/shared";
import { KPI_IMPORT_COPY } from "../../../lib/kpi-import-copy";
import {
  blockSheets,
  effectiveSheetYear,
  notesSheetStates,
  setSheetYear,
  type Page,
} from "../../../lib/kpi-import-wizard";
import { amberBox, btnSmall, inputStyle } from "../kpi-form-styles";

const m = KPI_IMPORT_COPY.mapping;

export function chip(active: boolean): React.CSSProperties {
  return {
    ...btnSmall,
    borderRadius: 999,
    ...(active
      ? {
          background: "color-mix(in srgb, var(--color-primary) 10%, white)",
          color: "var(--color-primary)",
          borderColor: "var(--color-primary)",
          fontWeight: 600,
        }
      : {}),
  };
}

export type MappingSaveState =
  | { kind: "idle" }
  | { kind: "saving" }
  | { kind: "saved" }
  | { kind: "failed"; message: string };

/** Sayfa sekmeleri (kural 7 — çok sayfalı dosya) + P2-8 sayfa atlama + çıkarım önerisi satırı. */
export function ImportSheetTabs(p: {
  sheetCounts: { name: string; count: number; idx: number }[];
  mappingCount: number;
  filterSheet: number | null;
  skippedSheets: readonly number[];
  suggestSkip: readonly number[];
  groupTotalSheets: readonly number[];
  grid: KpiImportGrid;
  onSelectSheet: (sheet: number | null) => void;
  onSetSkipped: (sheetIdx: number, skipped: boolean) => void;
}): JSX.Element {
  const { sheetCounts, filterSheet, skippedSheets, suggestSkip, groupTotalSheets, grid } = p;
  return (
    <>
      {sheetCounts.length > 1 ? (
        <div className="flex gap-2 flex-wrap items-center">
          <div role="tablist" aria-label="Sayfalar" className="flex gap-2 flex-wrap">
            <button
              type="button"
              role="tab"
              aria-selected={filterSheet === null}
              style={chip(filterSheet === null)}
              onClick={() => p.onSelectSheet(null)}
            >
              {m.sheetAll} ({p.mappingCount.toString()})
            </button>
            {sheetCounts.map((s) => (
              <button
                key={s.idx}
                type="button"
                role="tab"
                aria-selected={filterSheet === s.idx}
                style={{
                  ...chip(filterSheet === s.idx),
                  ...(skippedSheets.includes(s.idx)
                    ? { textDecoration: "line-through", color: "var(--color-text-3)" }
                    : {}),
                }}
                onClick={() => p.onSelectSheet(s.idx)}
              >
                {s.name} ({s.count.toString()})
                {sheetTag(grid, s.idx, skippedSheets.includes(s.idx))}
              </button>
            ))}
          </div>
          {filterSheet !== null ? (
            <button
              type="button"
              style={btnSmall}
              title={m.sheetSkipHelp}
              aria-pressed={skippedSheets.includes(filterSheet)}
              onClick={() => p.onSetSkipped(filterSheet, !skippedSheets.includes(filterSheet))}
              data-testid="kpi-import-sheet-skip"
            >
              {skippedSheets.includes(filterSheet) ? m.sheetUnskip : m.sheetSkip}
            </button>
          ) : null}
        </div>
      ) : null}
      <ImportSheetNotices grid={grid} skippedSheets={skippedSheets} />
      {suggestSkip.length > 0 ? (
        <div role="status" style={amberBox} data-testid="kpi-import-sheet-suggest-skip">
          <div className="font-semibold mb-1">{m.sheetSuggestSkipTitle(suggestSkip.length)}</div>
          <ul className="flex flex-col gap-1" style={{ paddingLeft: 16, margin: 0 }}>
            {suggestSkip.map((i) => (
              <li key={i} className="flex items-center gap-2 flex-wrap">
                <span>
                  {m.sheetSuggestSkipItem(grid.sheets[i]?.name ?? "", grid.sheets[i]?.skipHint)}
                </span>
                <button type="button" style={btnSmall} onClick={() => p.onSetSkipped(i, true)}>
                  {m.sheetSkip}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {groupTotalSheets.length > 0 ? (
        <div role="status" style={amberBox} data-testid="kpi-import-sheet-group-total">
          <ul className="flex flex-col gap-1" style={{ paddingLeft: 16, margin: 0 }}>
            {groupTotalSheets.map((i) => (
              <li key={i} className="flex items-center gap-2 flex-wrap">
                <span>
                  <strong>{grid.sheets[i]?.name ?? ""}</strong>: {m.groupTotalSheetHint}
                </span>
                <button type="button" style={btnSmall} onClick={() => p.onSetSkipped(i, true)}>
                  {m.sheetSkip}
                </button>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </>
  );
}

/** K13 — sekme etiketi eki: not sayfası ("not sayfası — atlandı") ya da düz atlama. */
function sheetTag(grid: KpiImportGrid, idx: number, skipped: boolean): string {
  if (grid.sheets[idx]?.kind === "notes")
    return ` · ${skipped ? m.notesSheetTag : m.notesSheetOpenTag}`;
  return skipped ? ` · ${m.sheetSkippedTag}` : "";
}

/** K13 — çok bloklu sayfa bilgisi + otomatik atlanan not sayfaları (tek sayfalı dosyada da). */
function ImportSheetNotices(p: {
  grid: KpiImportGrid;
  skippedSheets: readonly number[];
}): JSX.Element | null {
  const blocks = blockSheets(p.grid, p.skippedSheets);
  const notes = notesSheetStates(p.grid, p.skippedSheets).filter((n) => n.skipped);
  if (blocks.length === 0 && notes.length === 0) return null;
  return (
    <div
      role="status"
      className="flex flex-col gap-1"
      style={{ fontSize: "0.85rem", color: "var(--color-text-2)" }}
      data-testid="kpi-import-sheet-notices"
    >
      {blocks.map((b) => (
        <div key={`b${b.idx.toString()}`}>{m.blocksNotice(b.name, b.blocks)}</div>
      ))}
      {notes.length > 0 ? (
        <div>{m.notesSheetsNotice(notes.map((n) => n.name).join(", "))}</div>
      ) : null}
    </div>
  );
}

/** B-02/B-03 — dosyada olmayan yıl / işletme (zorunlu seçim, İleri kilidi); P2-6 yıl sayfa bazlı. */
export function ImportDefaultsFieldset(p: {
  askYearSheets: readonly number[];
  askEntitySheets: readonly number[];
  missingCount: number;
  grid: KpiImportGrid;
  defaults: KpiMappingDefaults;
  entityChoices: readonly ClientEntity[];
  onApplyDefaults: (next: KpiMappingDefaults) => void;
  onUpdateDefaults: (patch: KpiMappingDefaults) => void;
  onSetSkipped: (sheetIdx: number, skipped: boolean) => void;
}): JSX.Element | null {
  const { askYearSheets, askEntitySheets, missingCount, grid, defaults, entityChoices } = p;
  if (askYearSheets.length === 0 && askEntitySheets.length === 0) return null;
  const entityLabel = m.defaults.entityLabelForSheet(
    askEntitySheets.map((i) => grid.sheets[i]?.name ?? ""),
  );
  return (
    <fieldset
      className="rounded-xl p-3 flex gap-4 flex-wrap items-start"
      style={{
        border: `1px solid ${missingCount > 0 ? "var(--color-warning-border)" : "var(--color-border)"}`,
        background: missingCount > 0 ? "var(--color-warning-bg)" : "var(--color-surface)",
        margin: 0,
      }}
      data-testid="kpi-import-defaults"
    >
      <legend className="font-semibold text-sm px-1">{m.defaults.title}</legend>
      {askYearSheets.map((i) => {
        const sheetName = grid.sheets[i]?.name ?? `#${(i + 1).toString()}`;
        const label =
          grid.sheets.length > 1 ? m.defaults.yearSheetLabel(sheetName) : m.defaults.yearLabel;
        const year = effectiveSheetYear(defaults, i);
        return (
          <div key={i} className="flex flex-col gap-1 text-sm">
            <label className="flex flex-col gap-1">
              <span>
                {label}{" "}
                {year === undefined ? (
                  <span style={{ color: "var(--color-warning-text)" }}>
                    ({m.defaults.required})
                  </span>
                ) : null}
              </span>
              <input
                type="number"
                min={1990}
                max={2100}
                aria-label={label}
                style={{ ...inputStyle, width: 120, minHeight: 36 }}
                value={year ?? ""}
                onChange={(e) => {
                  const v = e.target.value === "" ? undefined : Number(e.target.value);
                  p.onApplyDefaults(
                    setSheetYear(
                      defaults,
                      i,
                      v !== undefined && Number.isInteger(v) ? v : undefined,
                    ),
                  );
                }}
              />
            </label>
            <span style={{ color: "var(--color-text-3)", fontSize: "0.75rem" }}>
              {grid.sheets.length > 1 ? m.defaults.yearSheetHint : m.defaults.yearHint}
            </span>
            {grid.sheets.length > 1 ? (
              <button
                type="button"
                style={{ ...btnSmall, alignSelf: "flex-start" }}
                onClick={() => p.onSetSkipped(i, true)}
              >
                {m.sheetSkip}
              </button>
            ) : null}
          </div>
        );
      })}
      {askEntitySheets.length > 0 ? (
        <label className="flex flex-col gap-1 text-sm">
          <span>
            {entityLabel}{" "}
            <span style={{ color: "var(--color-warning-text)" }}>({m.defaults.required})</span>
          </span>
          <select
            aria-label={entityLabel}
            style={{ ...inputStyle, width: 260, minHeight: 36 }}
            value={defaults.defaultEntityId ?? ""}
            onChange={(e) =>
              p.onUpdateDefaults({
                defaultEntityId: e.target.value === "" ? undefined : e.target.value,
              })
            }
          >
            <option value="">{m.defaults.entityNone}</option>
            {entityChoices.map((e) => (
              <option key={e.id} value={e.id}>
                {e.name}
              </option>
            ))}
          </select>
          <span style={{ color: "var(--color-text-3)", fontSize: "0.75rem" }}>
            {m.defaults.entityHint}
          </span>
        </label>
      ) : null}
    </fieldset>
  );
}

/** Sayfalama + otomatik kayıt durumu + "Taslağı kaydet". */
export function ImportMappingFooter(p: {
  pageData: Page<KpiMappingRow>;
  save: MappingSaveState;
  onPrev: () => void;
  onNext: () => void;
  onSaveDraft: () => void;
}): JSX.Element {
  const { pageData, save } = p;
  return (
    <div
      className="flex items-center gap-3 flex-wrap text-sm"
      style={{ color: "var(--color-text-3)" }}
    >
      <span>{m.pageLabel(pageData.from, pageData.to, pageData.total)}</span>
      <button type="button" style={btnSmall} disabled={pageData.page === 0} onClick={p.onPrev}>
        {m.prev}
      </button>
      <button
        type="button"
        style={btnSmall}
        disabled={pageData.page >= pageData.pages - 1}
        onClick={p.onNext}
      >
        {m.next}
      </button>
      <span style={{ flex: 1 }} />
      <span aria-live="polite">
        {save.kind === "saving"
          ? m.saving
          : save.kind === "saved"
            ? m.saved
            : save.kind === "failed"
              ? `${m.saveFailed}: ${save.message}`
              : m.autosaveNote}
      </span>
      <button
        type="button"
        style={btnSmall}
        onClick={p.onSaveDraft}
        disabled={save.kind === "saving"}
      >
        {m.saveDraft}
      </button>
    </div>
  );
}
